import { useEffect } from 'react';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import { useSEO } from '../../hooks/useSEO';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { ShieldCheck, Crosshair, Eye, Scale, Lock, Heart, Briefcase } from 'lucide-react';
import PrincipiosCarousel from '../landing/PrincipiosCarousel';

const values = [
  { icon: ShieldCheck, title: 'Confidencialidad', desc: 'La información sensible merece protección desde el primer minuto.' },
  { icon: Crosshair, title: 'Criterio', desc: 'No buscamos coincidencias. Buscamos encaje.' },
  { icon: Briefcase, title: 'Profesionalidad', desc: 'Experiencia contrastada, procesos rigurosos y compromiso con el resultado.' },
  { icon: Heart, title: 'Empatía', desc: 'Entendemos el contexto antes de proponer una solución.' },
];

const principles = [
  { icon: ShieldCheck, title: 'Confidencialidad por defecto', desc: 'Toda información se protege desde el primer contacto. Sin excepciones.' },
  { icon: Crosshair, title: 'Criterio sobre volumen', desc: 'Presentamos 3 perfiles, no 30. Cada recomendación está fundamentada.' },
  { icon: Eye, title: 'Transparencia en el proceso', desc: 'Empresa y profesional conocen las reglas desde el inicio. Sin agendas ocultas.' },
  { icon: Scale, title: 'Alineación de intereses', desc: 'Cobramos cuando se genera valor real. Nuestro éxito depende del suyo.' },
  { icon: Lock, title: 'Validación humana', desc: 'Cada conexión pasa por revisión profesional. La tecnología asiste, no decide.' },
];

const steps = [
  { num: '01', title: 'Diagnóstico', desc: 'Comprendemos el contexto, la necesidad y los objetivos estratégicos.',
    visual: <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <circle cx="34" cy="34" r="20" stroke="#C58C40" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.5"/>
      <circle cx="34" cy="34" r="10" stroke="#00163E" strokeWidth="1" opacity="0.3"/>
      <line x1="48" y1="48" x2="68" y2="68" stroke="#C58C40" strokeWidth="1.5" strokeLinecap="round" opacity="0.7"/>
      <circle cx="34" cy="34" r="3" fill="#C58C40" opacity="0.4"/>
    </svg>
  },
  { num: '02', title: 'Selección Top 3', desc: 'Identificamos los profesionales con mayor encaje para el mandato.',
    visual: <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <circle cx="40" cy="20" r="6" stroke="#C58C40" strokeWidth="1.5" opacity="0.7"/>
      <circle cx="22" cy="52" r="6" stroke="#C58C40" strokeWidth="1.5" opacity="0.7"/>
      <circle cx="58" cy="52" r="6" stroke="#C58C40" strokeWidth="1.5" opacity="0.7"/>
      <line x1="40" y1="26" x2="22" y2="46" stroke="#00163E" strokeWidth="1" opacity="0.2"/>
      <line x1="40" y1="26" x2="58" y2="46" stroke="#00163E" strokeWidth="1" opacity="0.2"/>
      <line x1="28" y1="52" x2="52" y2="52" stroke="#00163E" strokeWidth="1" opacity="0.2"/>
      <circle cx="40" cy="20" r="2.5" fill="#C58C40" opacity="0.5"/>
      <circle cx="22" cy="52" r="2.5" fill="#C58C40" opacity="0.5"/>
      <circle cx="58" cy="52" r="2.5" fill="#C58C40" opacity="0.5"/>
    </svg>
  },
  { num: '03', title: 'NDA', desc: 'Protegemos la información antes de compartirla.',
    visual: <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect x="24" y="32" width="32" height="28" rx="2" stroke="#C58C40" strokeWidth="1.5" opacity="0.6"/>
      <path d="M40 20 C32 20 28 26 28 32 L52 32 C52 26 48 20 40 20Z" stroke="#00163E" strokeWidth="1" opacity="0.25"/>
      <circle cx="40" cy="46" r="4" stroke="#C58C40" strokeWidth="1.5" opacity="0.7"/>
      <line x1="40" y1="50" x2="40" y2="54" stroke="#C58C40" strokeWidth="1.5" strokeLinecap="round" opacity="0.7"/>
    </svg>
  },
  { num: '04', title: 'Validación de Encaje', desc: 'Confirmamos que existe alineación real entre empresa y profesional.',
    visual: <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <circle cx="32" cy="40" r="18" stroke="#00163E" strokeWidth="1" opacity="0.2"/>
      <circle cx="48" cy="40" r="18" stroke="#C58C40" strokeWidth="1.5" opacity="0.5"/>
      <ellipse cx="40" cy="40" rx="8" ry="16" fill="#C58C40" opacity="0.08"/>
    </svg>
  },
  { num: '05', title: 'Activación del Mandato', desc: 'Formalizamos la colaboración y realizamos el seguimiento.',
    visual: <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <circle cx="24" cy="40" r="8" stroke="#00163E" strokeWidth="1" opacity="0.25"/>
      <circle cx="56" cy="40" r="8" stroke="#C58C40" strokeWidth="1.5" opacity="0.6"/>
      <line x1="32" y1="40" x2="48" y2="40" stroke="#C58C40" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.5"/>
      <path d="M52 36 L58 40 L52 44" stroke="#C58C40" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.7"/>
      <circle cx="24" cy="40" r="3" fill="#00163E" opacity="0.15"/>
      <circle cx="56" cy="40" r="3" fill="#C58C40" opacity="0.35"/>
    </svg>
  },
];

const faqs = [
  { q: '¿Cómo funciona el proceso?', a: 'Realizamos un diagnóstico de su necesidad. A partir de ese análisis, seleccionamos los 3 profesionales con mayor encaje. Todo opera bajo confidencialidad y NDA desde el primer momento.' },
  { q: '¿Qué diferencia a Conadvy de una consultora?', a: 'Conadvy no ejecuta proyectos de consultoría. Identifica y conecta a las personas adecuadas para aportar criterio en decisiones concretas.' },
  { q: '¿Por qué no utilizar simplemente mi red de contactos?', a: 'Las redes personales suelen estar limitadas por afinidad y accesibilidad. Conadvy amplía el acceso al criterio adecuado mediante un proceso estructurado y objetivo.' },
  { q: '¿Quién puede participar como profesional?', a: 'Profesionales con trayectoria contrastada a nivel ejecutivo: CEOs, board members, managing partners y especialistas senior. El proceso de evaluación es selectivo.' },
  { q: '¿El proceso es confidencial?', a: 'Sí. La confidencialidad es un pilar del protocolo. Tanto empresas como profesionales operan bajo acuerdos de no divulgación antes de cualquier intercambio de información.' },
  { q: '¿Cuál es el modelo de cobro?', a: 'Success fee: solo se genera compensación cuando la conexión se formaliza exitosamente. Sin costes anticipados ni compromisos fijos.' },
];

export default function ProtocoloPage() {
  useSEO({
    title: 'El Protocolo | Conadvy',
    description: 'Conozca el protocolo de confianza de Conadvy: NDA desde el primer contacto, perfiles verificados y proceso estructurado.',
    path: '/protocolo',
  });
  const sectionRef = useScrollAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="protocolo-page" className="min-h-screen bg-[#F5F5F3]">
      <Header variant="subpage" />

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-36 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="gold-line" />
            <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase">
              El Protocolo
            </span>
            <div className="gold-line" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-normal tracking-[0.02em] leading-tight text-[#00163E] mb-6">
            Cómo opera Conadvy
          </h1>
          <p className="text-base md:text-lg font-normal leading-relaxed text-slate-500 max-w-2xl mx-auto">
            Un proceso diseñado para generar confianza, proteger la información
            y conectar el criterio adecuado con cada desafío.
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section ref={sectionRef} className="pb-20 md:pb-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="space-y-0">
            {steps.map((step, i) => (
              <div
                key={step.num}
                data-testid={`protocolo-step-${i}`}
                className={`fade-up fade-up-delay-${Math.min(i + 1, 4)} flex items-center gap-8 py-10 ${
                  i < steps.length - 1 ? 'border-b border-[#00163E]/10' : ''
                }`}
              >
                <span className="text-[#C58C40] text-2xl font-light tracking-tight flex-shrink-0 w-12">
                  {step.num}
                </span>
                <div className="flex-1">
                  <h3 className="text-[#00163E] text-xl font-medium mb-2">{step.title}</h3>
                  <p className="text-slate-500 text-base leading-relaxed">{step.desc}</p>
                </div>
                <div className="hidden md:flex items-center justify-center flex-shrink-0 w-20 h-20">
                  {step.visual}
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-[#C58C40] text-sm tracking-[0.1em] uppercase mt-12">
            Respuesta en menos de 48 horas.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#00163E] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00163E] to-[#001F4F]" />
        <div className="grain absolute inset-0" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12">
          <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase block mb-8">
            Valores
          </span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {values.map((v, i) => (
              <div key={v.title} className="flex gap-5">
                <v.icon size={22} className="text-[#C58C40] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <h3 className="text-white text-lg font-medium mb-2">{v.title}</h3>
                  <p className="text-[#8A9BB1] text-sm leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-14 md:py-16 bg-[#F5F5F3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase block mb-4">
            Principios
          </span>
          <h2 className="text-2xl md:text-3xl font-normal text-[#00163E] mb-8">
            Principios del Protocolo Conadvy
          </h2>
          <PrincipiosCarousel items={principles} />
        </div>
      </section>

      {/* Perspective Block */}
      <section className="bg-[#00163E] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00163E] to-[#001F4F]" />
        <div className="grain absolute inset-0" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-white text-2xl md:text-3xl font-normal leading-relaxed mb-6">
            Las mejores decisiones suelen empezar con una perspectiva diferente.
          </p>
          <p className="text-[#8A9BB1] text-base leading-relaxed max-w-2xl mx-auto">
            Conadvy facilita el acceso a experiencia contrastada y conocimiento especializado
            cuando una empresa necesita avanzar con mayor claridad, velocidad y confianza.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase block mb-4">
            FAQ
          </span>
          <h2 className="text-2xl md:text-3xl font-normal text-[#00163E] mb-10">
            Preguntas frecuentes
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-b border-[#00163E]/10"
                data-testid={`protocolo-faq-${i}`}
              >
                <AccordionTrigger className="faq-trigger text-[#00163E] text-base font-normal py-6 hover:no-underline hover:text-[#C58C40] transition-colors">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-500 text-sm leading-relaxed pb-6">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer />
    </div>
  );
}
